import { safeAssert as assert } from '@upright/common/safe-assert';
import { Dictionary, difference, forEach, isNil, keys, max, min, uniq } from 'lodash';
import { allValueChains } from '../model/types';

export function assertStringType(value: string) {
  assert(typeof value === 'string', `"${value}" is not a string`);
}

const AssertionError = (msg: string) => Error(`Assertion error: ${msg}`);

export function assertEqualLength<T, U>(listA: T[], listB: U[]): void {
  if (listA.length !== listB.length) {
    throw AssertionError(`non-equal list lengths (${listA.length} vs {listB.length})`);
  }
}

export function assertNotNil<T>(value: T | undefined | null): T {
  if (isNil(value)) {
    throw Error(`Assertion error: value ${value} was nil`);
  }
  return value;
}

export function assertKeysAreValueChains<T>(object: Dictionary<T>): void {
  const nonValueChainKeys = difference(keys(object), allValueChains);
  if (nonValueChainKeys.length) {
    throw Error(`Assertion error: Object has non-value chain keys: ${nonValueChainKeys.join(', ')}`);
  }
}

export function assertMaxDeviation(collection: number[], maxDeviation: number, errorText?: string): void {
  const deviation = (max(collection) || 0) - (min(collection) || 0);
  if (deviation > maxDeviation) {
    const error = errorText || `Assertion error: values deviate ${deviation} when maximum is ${maxDeviation} `;
    throw Error(error);
  }
}

export function assertEqualBy<T>(collection: readonly T[], keyList: Array<keyof T>) {
  forEach(keyList, key => {
    assertEquals(
      collection.map(s => s[key]),
      String(key),
    );
  });
}

export function assertEquals<T>(collection: T[], description = '') {
  const unique = uniq(collection);
  assert(unique.length <= 1, `Assertion error: non-equal ${description} values ${JSON.stringify(unique)}`);
}

export function assertEqual<T>(item1: T, item2: T, itemName = '') {
  assert(
    item1 === item2,
    `Assertion error: ${itemName} ${JSON.stringify(item1)} doesn't equal ${JSON.stringify(item2)}`,
  );
}

export function assertUniqueBy<T>(collection: readonly T[], keyList: Array<keyof T>) {
  forEach(keyList, key => {
    assertUnique(
      collection.map(s => s[key]),
      String(key),
    );
  });
}

export function assertUnique<T>(collection: T[], description = '') {
  const unique = uniq(collection);
  assert(
    unique.length === collection.length,
    `Assertion error: non-unique ${description} values in ${JSON.stringify(collection)}`,
  );
}

export function assertNever(x: never): never {
  throw new Error(`Assertion error: unhandled value ${x as string}`);
}
